import { Injectable, NgZone } from '@angular/core';
import * as L from 'leaflet';
import { BehaviorSubject, Subject, filter } from 'rxjs';
import { CacheService } from '../../services/cache.service';
import { MapObjectReference } from './map-object';
import { MapFocusObject, ensureMapObjectPane } from './map-object-focus';
import { MapObjectPopover } from './map-object-popover';

import { MapViewport, mapViewport } from './spatial/map-viewport';

export interface MapLocation {
  readonly latitude: number;
  readonly longitude: number;
  readonly zoom?: number;
}

@Injectable({
  providedIn: 'root',
})
export class MapNavigationService {
  private map?: L.Map;
  private pendingLocation?: MapLocation;
  private currentFocus?: MapFocusObject;
  private focusRevision = 0;
  private readonly detailSource = new BehaviorSubject<MapObjectPopover | null>(null);
  readonly detail$ = this.detailSource.asObservable();
  private readonly focusRequestSource = new Subject<MapObjectReference>();
  readonly focusRequested$ = this.focusRequestSource.asObservable();
  private locatePending = false;
  get focusedObject(): MapObjectReference | null { return this.currentFocus?.reference ?? null; }

  /** Direct inspection also asks any mounted business list to reveal its record.
   * Camera-only locate requests do not feed back into the originating list. */
  private readonly revealSource = new Subject<MapObjectReference>();
  readonly revealRequests$ = this.revealSource.asObservable();

  hasObjectFocus(featureKey: string, objectId?: number | string): boolean {
    return this.focusedObject?.featureKey === featureKey
      && (objectId === undefined || this.focusedObject.objectId === objectId);
  }

  /** Callers resolve business data; navigation owns only presentation and camera. */
  focus(object: MapFocusObject | Promise<MapFocusObject>): Promise<void> {
    return this.present(object, false);
  }

  locate(object: MapFocusObject | Promise<MapFocusObject>): Promise<void> {
    return this.present(object, true);
  }

  clearFocus(): void {
    ++this.focusRevision;
    this.currentFocus = undefined;
    this.locatePending = false;
    this.detailSource.next(null);
  }

  /** Exact presentation identity prevents a late response for an earlier visit. */
  updateFocusPopup(object: MapFocusObject, popup: MapObjectPopover): void {
    if (this.currentFocus !== object) return;
    object.popup = popup;
    this.detailSource.next(popup);
  }

  private async present(input: MapFocusObject | Promise<MapFocusObject>, locate: boolean): Promise<void> {
    const revision = ++this.focusRevision;
    let object: MapFocusObject;
    try {
      object = input instanceof Promise ? await input : input;
    } catch (error) {
      if (revision !== this.focusRevision) return;
      throw error;
    }
    if (revision !== this.focusRevision) return;
    this.pendingLocation = undefined;
    this.detailSource.next(null);
    this.currentFocus = object;
    if (!locate) this.revealSource.next(object.reference);
    this.focusRequestSource.next(object.reference);
    this.locatePending = locate;
    this.renderFocus();
  }

  private renderFocus(): void {
    const map = this.map;
    const object = this.currentFocus;
    if (!object) return;
    this.detailSource.next(object.popup);
    if (!map || !this.locatePending) return;
    const geometry = L.geoJSON(object.geometry, {
      pointToLayer: (_feature, at) => L.marker(at, {
        ...(object.pointIcon ? { icon: object.pointIcon } : {}), interactive: false, keyboard: false,
      }),
    });
    let bounds = geometry.getBounds();
    if (!bounds.isValid()) throw new Error('Map object has no displayable geometry.');
    // GeoJSON splits dateline-crossing polygons at +/-180. Render their narrow
    // continuous extent instead of fitting almost the entire world.
    const wrapped = bounds.getEast() - bounds.getWest() > 180;
    if (wrapped) {
      geometry.options.coordsToLatLng = ([lng, lat]) => L.latLng(lat, lng < 0 ? lng + 360 : lng);
      geometry.clearLayers().addData(object.geometry);
      bounds = geometry.getBounds();
    }
    if (this.locatePending) {
      this.fitBounds(bounds, { maxZoom: 17 });
      this.locatePending = false;
    }
  }

  /** The latest settled viewport, owned here and shared with map features. */
  private readonly viewportSource = new BehaviorSubject<MapViewport | null>(null);
  readonly viewport$ = this.viewportSource.pipe(filter((value): value is MapViewport => value !== null));

  /** Settled zoom for readouts; viewport$ also includes settled map bounds. */
  private readonly zoomSource = new BehaviorSubject<number>(13);
  readonly zoom$ = this.zoomSource.asObservable();

  constructor(cache: CacheService, private readonly zone?: NgZone) {
    cache.sessionChanged$.subscribe(() => {
      this.viewportSource.next(null);
      this.pendingLocation = undefined;
      this.clearFocus();
    });
  }

  private readonly onUnload = () => { if (this.map) this.detach(this.map); };

  attach(map: L.Map): void {
    if (this.map === map) return;
    if (this.map) this.detach(this.map);
    this.map = map;
    ensureMapObjectPane(map, 'map-object-selection', 620);
    map.on('unload', this.onUnload);
    this.zoomSource.next(map.getZoom());
    map.on('zoomend', this.publishZoom, this);
    if (this.pendingLocation) this.applyLocation(this.pendingLocation);
    this.renderFocus();
  }

  detach(map: L.Map): void {
    if (!map || this.map !== map) return;
    map.off('zoomend', this.publishZoom, this);
    map.off('unload', this.onUnload);
    this.clearFocus();
    this.map = undefined;
  }

  /** Capture the actual camera, including a pan still awaiting its debounce. */
  captureView(): MapLocation | null {
    if (!this.map) return this.pendingLocation ?? null;
    const center = this.map.getCenter();
    return { latitude: center.lat, longitude: center.lng, zoom: this.map.getZoom() };
  }

  /** History restores presentation only; it does not request object selection. */
  restoreView(location: MapLocation | null): void {
    this.clearFocus();
    if (!location) return;
    this.pendingLocation = { ...location };
    this.applyLocation(location);
  }

  /** Execute a navigation command from any UI surface. */
  navigateTo(location: MapLocation): void {
    this.clearFocus();
    this.pendingLocation = location;
    this.applyLocation(location);
  }

  fitBounds(
    bounds: L.LatLngBoundsExpression,
    options?: L.FitBoundsOptions,
  ): void {
    const map = this.map;
    if (!map) return;

    const rect = map.getContainer().getBoundingClientRect();
    const dock = document.querySelector<HTMLElement>('.ws-card--dock-right')?.getBoundingClientRect();
    const sidebar = document.querySelector<HTMLElement>('.sidebar')?.getBoundingClientRect();
    const left = sidebar && sidebar.right > rect.left && sidebar.left <= rect.left
      ? Math.max(0, sidebar.right - rect.left) : 0;
    const right = dock && dock.width > 0 && dock.left - rect.left - left >= 320
      ? Math.max(0, rect.right - dock.left) : 0;
    const centerX = rect.left + (left + rect.width - right) / 2;
    const composer = document.querySelector<HTMLElement>('.composer-card')?.getBoundingClientRect();
    const bottom = composer && composer.width > 0 && composer.height > 0
        && composer.left < centerX && composer.right > centerX
        && composer.top > rect.top && composer.top < rect.bottom
      ? rect.bottom - composer.top : 0;
    map.fitBounds(bounds, {
      paddingTopLeft: L.point(left + 32, 48),
      paddingBottomRight: L.point(right + 32, bottom + 32),
      ...options,
    });
  }

  invalidateSize(): void {
    this.map?.invalidateSize();
  }

  /** Capture the Leaflet view after MapComponent's settle/debounce policy. */
  publishViewport(): void {
    if (!this.map) return;
    const zoom = this.map.getZoom();
    this.zoomSource.next(zoom);
    this.viewportSource.next(mapViewport(this.map.getBounds(), zoom));
  }

  private applyLocation(location: MapLocation): void {
    if (!this.map) return;
    this.map.setView(
      [location.latitude, location.longitude],
      location.zoom ?? this.map.getZoom(),
    );
  }

  private publishZoom(): void {
    if (!this.map) return;
    const publish = () => this.zoomSource.next(this.map!.getZoom());
    if (this.zone) this.zone.run(publish);
    else publish();
  }
}
